import { useState } from "react";
import { ShoppingCart, Bike, Package, AlertTriangle, ClipboardList, TrendingUp, TrendingDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SubdealerInventory } from "@/components/dashboard/SubdealerInventory";
import { PendingBackendTable } from "@/components/dashboard/PendingBackendTable";

const Card = ({ icon: Icon, value, label, color, testId, sub, onSubClick }) => (
  <div data-testid={testId} className="min-w-[128px] flex-1 bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-3 text-center transition-all hover:border-blue-500/50">
    <Icon className={`h-4 w-4 mx-auto ${color}`} />
    <p className={`text-xl font-extrabold font-mono mt-1 ${color}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
    <p className="text-[10px] font-bold text-slate-400 mt-0.5 uppercase tracking-wide">{label}</p>
    {sub && (onSubClick ? (
      <button data-testid={`${testId}-sub-btn`} onClick={onSubClick}
        className="text-[9px] text-blue-400 underline underline-offset-2 hover:text-blue-300">{sub}</button>
    ) : (
      <p className="text-[9px] text-slate-500">{sub}</p>
    ))}
  </div>
);

export const KpiRow = ({ kpis }) => {
  const [subOpen, setSubOpen] = useState(false);
  const [pendOpen, setPendOpen] = useState(false);
  if (!kpis) return <div className="h-24 bg-[#1E293B] border border-[#334155] rounded-xl animate-pulse" />;
  const g = kpis.growth || {};
  const pb = kpis.pending_backend;
  const pct = (x) => (x?.pct == null ? "—" : `${x.pct >= 0 ? "▲" : "▼"}${Math.abs(x.pct)}%`);
  const gc = (x) => (x?.pct == null ? "text-slate-500" : x.pct >= 0 ? "text-emerald-400" : "text-rose-400");
  return (
    <div className="space-y-2">
    <div data-testid="kpi-row" className="flex gap-2.5 overflow-x-auto pb-1 no-scrollbar">
      <Card testId="kpi-purchased" icon={ShoppingCart} value={kpis.total_purchased?.toLocaleString("en-IN")} label="Purchased" color="text-blue-400" />
      <Card testId="kpi-delivered" icon={Bike} value={kpis.total_units_sold?.toLocaleString("en-IN")} label="Delivered" color="text-slate-100" />
      <Card testId="kpi-stock" icon={Package} value={kpis.in_stock_count} label="Stock" color="text-amber-400"
        sub={`+${kpis.subdealer_stock ?? 0} at subdealers`} onSubClick={() => setSubOpen(true)} />
      <Card testId="kpi-old-stock" icon={AlertTriangle} value={kpis.old_stock_alerts} label="Old 60+ days" color="text-rose-400" sub={`${kpis.old_stock_count} aged 90+`} />
      <Card testId="kpi-rto-pending" icon={ClipboardList} value={kpis.rto_pending} label="RTO Pending" color="text-violet-400" />
      <Card testId="kpi-growth-counter" icon={TrendingUp} value={pct(g.counter)} label="Counter YoY" color={gc(g.counter)} sub={g.counter ? `${g.counter.current} vs ${g.counter.previous}` : ""} />
      <Card testId="kpi-growth-subdealer" icon={TrendingDown} value={pct(g.subdealer)} label="Subdealer YoY" color={gc(g.subdealer)} sub={g.subdealer ? `${g.subdealer.current} vs ${g.subdealer.previous}` : ""} />
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent data-testid="subdealer-drilldown-dialog" className="max-w-3xl w-[95vw] max-h-[85vh] overflow-y-auto bg-[#0B0F17] border-[#334155] text-slate-100 p-0">
          <DialogHeader className="px-4 pt-4 pb-0">
            <DialogTitle className="text-base" style={{ fontFamily: "Outfit, sans-serif" }}>Subdealer Holdings</DialogTitle>
          </DialogHeader>
          <div className="px-2 pb-3">
            {subOpen && <SubdealerInventory fq="" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    {pb && (
      <div>
        <button data-testid="pending-backend-line" onClick={() => pb.count > 0 && setPendOpen((v) => !v)}
          className={`w-full text-left rounded-lg border px-3 py-2 text-[11px] font-semibold transition-colors ${
            pb.count > 0 ? "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 cursor-pointer"
                         : "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"}`}>
          {pb.count > 0 ? `⚠ ${pb.count} chassis pending to be marked as delivered/sold in the backend ${pendOpen ? "▲" : "▼"}`
                        : "✓ 0 chassis pending to be marked as delivered/sold in the backend"}
        </button>
        {pendOpen && pb.count > 0 && <PendingBackendTable items={pb.items} />}
      </div>
    )}
    </div>
  );
};
